import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener } from '@angular/core';
import { LoadingController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { ActivityService } from '@omni/services/activity/activity.service';
import { DeviceService } from '@omni/services/device/device.service';
import { EventsService } from '@omni/services/events/events.service';
import { GoAppNavigationService } from '@omni/services/go-app-navigation.service';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { UIService } from '@omni/services/ui/ui.service';
import { Subject, takeUntil } from 'rxjs';

const COLORED_NAV_ICONS = false;

const NAV_ICON_DOMAIN: Readonly<Record<string, string>> = {
  dashboard: 'data',
  accounts: 'data',
  contacts: 'data',
  marketscan: 'data',
  'case-tool': 'data',
  survey: 'data',
  opportunitymgmt: 'commercial',
  orderManagement: 'commercial',
  quotes: 'commercial',
  allocation: 'commercial',
  resources: 'commercial',
  presentations: 'commercial',
  'message-templates': 'commercial',
  analytics: 'commercial',
  eventstool: 'commercial',
  fieldMaterialManagement: 'commercial',
  accountManagement: 'planning',
  marketingBusiness: 'planning',
  'call-plan': 'planning',
  scheduler: 'planning',
  'time-off': 'planning',
  coaching: 'planning',
  consent: 'regulated',
  'medical-inquiry': 'regulated',
  scientificactivity: 'regulated',
  pharmacovigilance: 'safety',
};

@Component({
  selector: 'go-app-navigation-menu',
  templateUrl: './go-app-navigation-menu.component.html',
  styleUrl: './go-app-navigation-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GoAppNavigationMenuComponent {
  isExpanded = false;

  public sideBarOptions = [];
  private readonly destroy$ = new Subject();
  private selectedView: string = 'dashboard';

  constructor(private translate: TranslateService,
    public readonly device: DeviceService,
    private readonly goAppNavigationService: GoAppNavigationService,
    private readonly navService: NavigationService,
    private readonly _cd: ChangeDetectorRef,
    private readonly events: EventsService,
    private readonly el: ElementRef,
    private readonly activityService: ActivityService,
    private readonly loadingCtrl: LoadingController,
    private readonly uiService: UIService,
  ) {
  }

  ngOnInit() {
    this.translate.onLangChange.subscribe(data => {
      this.translate.use(data.lang);
    });

    this.events.subscribe('navigateTo', (id, navigation?: { opened: boolean }) => {
      const option = this.sideBarOptions.find(op => op.id === id);
      if (option) {
        this.onClickMenuOption(option);
        if (navigation) navigation.opened = true;
      }
    });
    this.events.subscribe('clearSelectedView', () => {
      this.selectedView = undefined;
      this.sideBarOptions.forEach(op => op.selected = false);
      this._cd.markForCheck();
    });
    this.events.subscribe('setSelectedNavView', (view: string) => {
      this.selectedView = view;
      this.sideBarOptions.forEach(op => op.selected = op.id === view);
      this._cd.markForCheck();
    });
    this.goAppNavigationService.menuOptions
      .pipe(takeUntil(this.destroy$))
      .subscribe(options => {
        this.sideBarOptions = [];
        this.sideBarOptions.push(
          ...this.getDefaultMenuOptions(),
          ...options);
        this.sideBarOptions.forEach(op => {
          op.selected = op.id === this.selectedView;
          op.iconDomain = COLORED_NAV_ICONS ? NAV_ICON_DOMAIN[op.id] : null;
        });
        this._cd.markForCheck();
      });
  }

  private getDefaultMenuOptions() {
    return [{
      id: 'hamburger',
      visible: true,
      iconName: 'hamburger-icon',
      iconPath: 'assets/imgs/nav/hamburger.svg',
      displayName: "",
      // The toggle shows no label in either state, so it needs its own accessible name.
      ariaLabel: this.translate.instant('NAVIGATION_MENU'),
      selected: false,
      component: '',
      enable: true
    },
    {
      id: 'dashboard',
      visible: true,
      iconName: 'home-icon',
      iconPath: 'assets/imgs/nav/dashboard.svg',
      displayName: this.translate.instant('HOME'),
      selected: false,
      component: 'HomePage',
      enable: true
    }];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (target.closest('.menu-options')) {
      return;
    }
    this.toggleSideBar(false);
  }

  private toggleSideBar(isExpanded: boolean) {
    this.isExpanded = isExpanded;
    const home = (this.el.nativeElement as HTMLElement).closest('page-home');
    home.classList.toggle('overlay-active', this.isExpanded);
    this._cd.markForCheck();
  }

  async onClickMenuOption(option) {
    if(this.selectedView === 'eventstool' && option.id === 'eventstool') return;
    if (option.id !== 'hamburger') {
      this.selectedView = option.id;
    }
    let loading: HTMLIonLoadingElement;
    switch (option.id) {
      case "hamburger":
        this.toggleSideBar(!this.isExpanded);
        break;
      case "dashboard":
        this.activityService.clearAgendaFilters();
        this.sideBarOptions.forEach(op => op.selected = 'dashboard' == op.id);
        this.activityService.selectedActivity = null;
        this.uiService.showRightPane = false;
        this.events.publish('selected-nav-view', option.id);
        await this.navService.setRootWithTracking(PageName.GoAppActivitiesPageComponent, PageName.GoAppActivitiesPageComponent, { __source: 'dashboard' }, PageName.GoAppActivitiesPageComponent);
        this.toggleSideBar(false);
        break;
      default:
        try {

          this.activityService.activityOpenedFromTodo = false;
          if (option.component !== 'HyperlinkExtURL' || option.openMode === 1) {
            this.sideBarOptions.forEach(op => op.selected = option.id == op.id);
          } else {
            this.selectedView = undefined;
          }
          await this.navService.popToRootWithPageTracking();
          await this.goAppNavigationService.navigateToMenuOption(option);
          this.toggleSideBar(false);
        } finally {
          loading?.dismiss();
        }
    }
    this._cd.markForCheck();
  }

  onTooltipVisibilityChange() {
    this._cd.markForCheck();
  }

  trackByOptionId(index: number, option: any): string {
    return option?.id ?? index;
  }

  getIconPath(option) {
    return option.selected && option.selectedIconPath ? option.selectedIconPath : option.iconPath;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
